import React, { useEffect, useState } from "react";
import { formatMessage, formatMessageWithParams } from "../../utils/helpers/LanguageHelper";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import FooterPanel from "../layout/FooterPanel";
import { AdvancedDocumentPanel } from "./AdvancedDocumentPanel";
import { AdvancedDocumentMetadata } from "../../utils/helpers/DocumentAdvancedCustomXmlHelper";
import { InfoDocumentPanel } from "./InfoDocumentPanel";
import { PivotObject } from "../generic/PivotObject";
import { ObjectPanelTabs } from "../coobjects/co-panel/ObjectPanelComponent";
import { DocumentParameters, ParametersDocument } from "./ParametersDocument";
import { checkForDuplicate, getDocumentHasAttachment } from "../../api/document/Documents";
import { normalizeDocumentName } from "../../utils/helpers/ObjectCheckerHelper";
import { normalizePath } from "../../redux/feature/document/document-slice";
import { CustomModal } from "../layout/ActionModal";
import { getUpdatedDocumentParameters } from "../../redux/feature/cpm/map-objects";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import LoadingManager from "../../common/loading/LoadingManager";

export type EditOperationType = "DUPLICATE" | "DETACH" | "DETACHFAKE" | "IMPORTNONTAGETIK";

interface EditDocumentProps {
  onLastBackButton: () => void | undefined;
  onBackButton?: () => void;
  isEdit: boolean;
  editDocumentOperationType?: EditOperationType;
  onSave: (state: DocumentData) => Promise<void>;
  fatherPartId?: string;
  getDocumentPartDetails: () => Promise<{
    documentData: DocumentData;
    fatherDocumentParameters: DocumentParameters[];
    sourceDocumentName?: string;
  }>;
  onUpdateDocumentInfo?: (infoDocState: DocumentData) => void;
  onTabChange?: (selectedTabIndex: string) => void;
}

export interface DocumentData {
  documentId: string;
  description: string;
  name: string;
  advanced?: AdvancedDocumentMetadata;
  parameters?: DocumentParameters[];
  root: boolean;
  multilinked: boolean;
  path: string;
  fatherPartId?: string;
  language: string;
  duplicateAttachments?: boolean;
}

interface EditDocumentState {
  key: string;
  tabs: ObjectPanelTabs[];
  documentData: DocumentData;
  isModified: boolean;
  fatherDocumentParameters: DocumentParameters[] | undefined;
  duplicateDocumentsNames: string[];
  sourceDocumentName?: string;
  askForAttachmentsDuplication?: boolean;
}

const getTabsList = (
  isEdit: boolean,
  isRoot: boolean,
  isMultilinked: boolean,
  operation: EditOperationType
): ObjectPanelTabs[] => {
  let tabs = [
    {
      key: "1",
      name: "cof.documentproperties.generalinfo",
      isValid: isEdit || operation === "IMPORTNONTAGETIK",
    },
    {
      key: "2",
      name: "cof.createnewobject.parameters",
      isValid: isEdit,
    },
  ];

  if (isRoot || isMultilinked) {
    tabs.push({
      key: "3",
      name: "cof.createnewobject.advanced",
      isValid: isEdit,
    });
  }
  return tabs;
};

const isSaveButtonDisabled = (
  tabs: ObjectPanelTabs[],
  isModified: boolean,
  isDuplicateOrDetach?: EditOperationType
) => {
  if (isDuplicateOrDetach === "DETACHFAKE") return false;
  return !(
    isModified &&
    tabs.reduce<boolean>((acc, tab) => {
      return acc && tab.isValid;
    }, true)
  ); //all tab valid
};

const isNextButtonDisabled = (tabs: ObjectPanelTabs[], currentKey: string) => {
  const thisTab = tabs.find((tab) => tab.key === currentKey);
  return !thisTab?.isValid;
};
const isSaveButtonVisible = (tabs: ObjectPanelTabs[], currentKey: string, isEdit: boolean) => {
  const lastTab = tabs[tabs.length - 1];
  return isEdit || lastTab?.key === currentKey; //if we're at the last tab then next is not available
};

const isOnFirstTab = (tabs: ObjectPanelTabs[], currentKey: string) => {
  const firstTab = tabs[0];
  return firstTab?.key === currentKey;
};

export const EditDocument: React.FunctionComponent<EditDocumentProps> = (props) => {
  const [state, setState] = useState<EditDocumentState>({
    key: "1",
    documentData: null,
    tabs: [],
    isModified: false,
    fatherDocumentParameters: [],
    duplicateDocumentsNames: [],
  });
  useEffect(() => {
    LoadingManager.create("getInitialDocumentPartDetails");
    getInitialDocumentPartDetails().finally(() => LoadingManager.remove("getInitialDocumentPartDetails"));
  }, []);

  async function getInitialDocumentPartDetails() {
    const { documentData, fatherDocumentParameters, sourceDocumentName } = await props.getDocumentPartDetails();
    const updatedParams = getUpdatedDocumentParameters(documentData.parameters, fatherDocumentParameters);
    const askForAttachmentsDuplication =
      props.editDocumentOperationType === "DUPLICATE"
        ? (await getDocumentHasAttachment(documentData.documentId)) ?? false
        : undefined;

    setState((s) => ({
      ...s,
      isModified: !props.isEdit,
      documentData: { ...documentData, parameters: updatedParams },
      fatherDocumentParameters,
      sourceDocumentName,
      tabs: getTabsList(props.isEdit, documentData.root, documentData.multilinked, props.editDocumentOperationType),
      askForAttachmentsDuplication,
    }));
  }

  function onClickPivot(selected) {
    setState((s) => ({ ...s, key: selected.props.itemKey }));
    props.onTabChange?.(selected.props.itemKey);
  }

  function updateDocumentInfo(infoDocState: DocumentData, isValid: boolean) {
    setState((s) => {
      const newTabs: ObjectPanelTabs[] = [...s.tabs];
      newTabs[0].isValid = isValid;
      props.onUpdateDocumentInfo?.(infoDocState);
      return {
        ...s,
        documentData: {
          ...s.documentData,
          language: infoDocState.language,
          name: infoDocState.name,
          path: infoDocState.path,
          description: infoDocState.description,
          duplicateAttachments: infoDocState.duplicateAttachments,
        },
        tabs: newTabs,
        isModified: true,
      };
    });
  }

  function updateDocumentParameters(parameters: DocumentParameters[], isValid: boolean, isModified: boolean) {
    setState((s) => {
      const newTabs: ObjectPanelTabs[] = [...s.tabs];
      newTabs[1].isValid = isValid;
      return {
        ...s,
        documentData: {
          ...s.documentData,
          parameters: parameters,
        },
        tabs: newTabs,
        isModified: isModified || s.isModified,
      };
    });
  }

  function updateDocumentAdvanced(advancedDocState: AdvancedDocumentMetadata, isValid: boolean) {
    setState((s) => {
      const newTabs: ObjectPanelTabs[] = [...s.tabs];
      newTabs[2].isValid = isValid;
      return {
        ...s,
        documentData: {
          ...s.documentData,
          advanced: {
            ...s.documentData.advanced,
            numberFormat: advancedDocState.numberFormat,
            typeNumber: advancedDocState.typeNumber,
            percentageFormat: advancedDocState.percentageFormat,
            typePercentage: advancedDocState.typePercentage,
            dateFormat: advancedDocState.dateFormat,
            typeDate: advancedDocState.typeDate,
            imageExtension: advancedDocState.imageExtension,
            imageResolution: advancedDocState.imageResolution,
            templateInfo: advancedDocState.templateInfo,
            locale: advancedDocState.locale,
          },
        },
        tabs: newTabs,
        isModified: true,
      };
    });
  }

  const getPanel = () => {
    switch (state.key) {
      case "1":
        return (
          <InfoDocumentPanel
            documentInfo={state.documentData}
            updateDocumentInfo={updateDocumentInfo}
            isEdit={props.isEdit}
            editDocumentOperationType={props.editDocumentOperationType}
            removeLocale={!state.documentData?.root && !state.documentData?.multilinked}
            isDuplicate={props.editDocumentOperationType === "DUPLICATE"}
            askForAttachmentsDuplication={state.askForAttachmentsDuplication}
          />
        );
      case "2":
        return (
          <ParametersDocument
            updateDocumentParameters={updateDocumentParameters}
            documentParameters={state.documentData.parameters ?? []}
            canInherit={!state.documentData?.root && !state.documentData.multilinked}
            fatherDocumentParameters={state.fatherDocumentParameters}
          />
        );
      case "3":
        return (
          <AdvancedDocumentPanel
            documentId={state.documentData.documentId}
            isDuplicate={props.editDocumentOperationType === "DUPLICATE"}
            documentAdvanced={state.documentData.advanced}
            updateDocumentAdvanced={updateDocumentAdvanced}
            isEdit={props.isEdit}
            parameters={state.documentData.parameters}
            goToParametersPage={onBackButtonClicked}
          />
        );
      default:
        return null;
    }
  };

  const onSaveButton = () => {
    const filteredParameters = state.documentData.parameters?.reduce<DocumentParameters[]>((acc, el) => {
      const dimensions = el.dimensions.reduce((dims, dim) => {
        if (dim.values.length) return [...dims, dim];
        return dims;
      }, []);
      if (dimensions.length) return [...acc, { ...el, dimensions }];
      return acc;
    }, []);

    const advanced = state.documentData.advanced;
    if (!advanced.locale) advanced.locale = state.documentData.language;
    if (!advanced.templateInfo || advanced.templateInfo.templateId == "default") advanced.templateInfo = null;

    props
      .onSave({
        ...state.documentData,
        advanced,
        parameters: filteredParameters,
        fatherPartId: props.fatherPartId ? props.fatherPartId : "root",
      })
      .then(() => {
        getInitialDocumentPartDetails();
      });
  };

  if (state.duplicateDocumentsNames && state.duplicateDocumentsNames.length) {
    const titleId =
      props.editDocumentOperationType === "DUPLICATE"
        ? "cof.documentmanagement.outline.duplicate.tile.duplicatedocument"
        : "cof.documentmanagement.outline.detach.tile.detachdocument";

    return (
      <CustomModal
        modalTitle={formatMessage(titleId) + (state.sourceDocumentName ? ` - ${state.sourceDocumentName}` : "")}
        messageTitle={formatMessage("cof.documentmanagement.outline.duplicate.tile.alreadyexists")}
        message={formatMessageWithParams("cof.documentmanagement.outline.duplicate.message.alreadyexists", {
          documentName: "<br/>" + state.duplicateDocumentsNames.join(";<br/>") + ".<br/>",
        })}
        type="confirm"
        onHide={() => {}}
        actions={[
          {
            title: formatMessage("cof.base.edit"),
            onClick: () => {
              setState((s) => ({
                ...s,
                duplicateDocumentsNames: [],
              }));
            },
            variant: "white",
          },
        ]}
      />
    );
  }

  const getSaveButtonText = () => {
    if (props.editDocumentOperationType === "DUPLICATE") return "cof.base.rollforward";
    if (props.editDocumentOperationType === "DETACH" || props.editDocumentOperationType === "DETACHFAKE")
      return "cof.base.detach";
    if (props.isEdit) return "cof.base.save";
    return "cof.base.create";
  };

  const getSaveButtonId = () => {
    if (props.editDocumentOperationType === "DUPLICATE") return "duplicate_button";
    if (props.editDocumentOperationType === "DETACH" || props.editDocumentOperationType === "DETACHFAKE")
      return "detach_button";
    if (props.isEdit) return "save_button";
    return "create_button";
  };

  const onNextButtonClick = async () => {
    let duplicateDocumentsNames = [];

    if (
      (props.editDocumentOperationType === "DETACH" ||
        props.editDocumentOperationType === "DUPLICATE" ||
        props.editDocumentOperationType === "DETACHFAKE") &&
      isOnFirstTab(state.tabs, state.key)
    ) {
      duplicateDocumentsNames = await checkForDuplicate({
        documentId: state.documentData.documentId,
        documentName: normalizeDocumentName(state.documentData.name),
        path: normalizePath(state.documentData.path),
        duplicateAttachments: state.documentData.duplicateAttachments ?? false,
      });
    }

    let newTabKey;

    setState((s) => {
      newTabKey = duplicateDocumentsNames.length ? s.key : (Number(s.key) + 1).toString();
      return { ...s, key: newTabKey, duplicateDocumentsNames: duplicateDocumentsNames ?? [] };
    });
    props.onTabChange?.(newTabKey);
  };

  const onBackButtonClicked = () => {
    if (props.onBackButton) {
      props.onBackButton();
      return;
    }
    if (isOnFirstTab(state.tabs, state.key)) {
      props.onLastBackButton();
      return;
    }
    let newTabKey;
    setState((s) => {
      newTabKey = (Number(s.key) - 1).toString();
      return { ...s, key: newTabKey };
    });
    props.onTabChange?.(newTabKey);
  };

  return (
    <div className="ms-Grid" dir="ltr">
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          <PivotObject
            isEdit={props.isEdit}
            selectedKey={Number(state.key)}
            onClickPivot={onClickPivot}
            objectType={"DOCUMENT"}
            items={state.tabs}
            defaultTabName={formatMessage("cof.documentproperties.generalinfo")}
          />
          {getPanel()}
        </div>
      </div>
      <FooterPanel
        scrollViewNavigationProps={
          !props.isEdit && state.tabs.length > 1
            ? { activeLink: state.key.toString(), items: state.tabs, isClickEnabled: false }
            : undefined
        }
      >
        {(!isOnFirstTab(state.tabs, state.key) || props.onLastBackButton) && (
          <PrimaryButton
            id="previous_button"
            data-testid="Back"
            className={ButtonVariants.white}
            text={formatMessage("cof.base.previous")}
            onClick={onBackButtonClicked}
            style={{ float: "left" }}
          />
        )}
        {isSaveButtonVisible(state.tabs, state.key, props.isEdit) ? (
          <PrimaryButton
            id={getSaveButtonId()}
            data-testid="Save"
            className={ButtonVariants.primary}
            disabled={isSaveButtonDisabled(state.tabs, state.isModified, props.editDocumentOperationType)}
            text={formatMessage(getSaveButtonText())}
            onClick={onSaveButton}
            style={{ float: "right" }}
          />
        ) : (
          <PrimaryButton
            id="next_button"
            data-testid="Next"
            className={ButtonVariants.primary}
            text={formatMessage("cof.base.next")}
            onClick={onNextButtonClick}
            disabled={isNextButtonDisabled(state.tabs, state.key)}
            style={{
              float: "right",
            }}
          />
        )}
      </FooterPanel>
    </div>
  );
};
